/**
 * Nexera Plateforme - Mobile Design Tokens
 *
 * Following Material Design guidelines and 2024 mobile best practices.
 * These tokens extend the existing design system with mobile-specific values.
 *
 * References:
 * - Material Design Touch Targets: https://m2.material.io/design/usability/accessibility.html#layout-and-typography
 * - Material Design Bottom Navigation: https://m2.material.io/components/bottom-navigation
 * - iOS Human Interface Guidelines: https://developer.apple.com/design/human-interface-guidelines/layout
 */

/**
 * Touch Target Sizes
 * Minimum: 48x48px (Material Design standard)
 * Comfortable: 56x56px (recommended for primary actions)
 */
export const touchTargets = {
  /** Minimum touch target size (48x48px) - Material Design standard */
  min: 48,
  /** Comfortable touch target size (56x56px) - recommended for primary actions */
  comfortable: 56,
  /** Small touch targets for dense UIs (44x44px) - iOS minimum */
  small: 44,
} as const;

/**
 * Bottom Navigation Dimensions
 * Following Material Design specifications
 */
export const bottomNav = {
  /** Height of bottom navigation bar */
  height: 56,
  /** Height when using large icons (optional) */
  heightLarge: 64,
  /** Icon size */
  iconSize: 24,
  /** Label font size */
  labelSize: 12,
  /** Active indicator height (Material Design 3) */
  activeIndicatorHeight: 32,
  /** Maximum number of items (Material Design recommendation) */
  maxItems: 5,
  /** Minimum number of items */
  minItems: 3,
  /** Item min width */
  itemMinWidth: 80,
  /** Item max width */
  itemMaxWidth: 168,
} as const;

/**
 * Top AppBar Dimensions
 * Material Design mobile specifications
 */
export const topBar = {
  /** Height of mobile top bar */
  height: 56,
  /** Height with large title */
  heightLarge: 64,
  /** Icon button size */
  iconSize: 24,
  /** Horizontal padding */
  paddingX: 16,
  /** Spacing between elements */
  gap: 8,
} as const;

/**
 * Mobile-Specific Spacing Scale
 * Optimized for touch interfaces and smaller screens
 */
export const mobileSpacing = {
  /** Extra small spacing (4px) - tight spacing */
  xs: 4,
  /** Small spacing (8px) - compact elements */
  sm: 8,
  /** Medium spacing (16px) - default spacing */
  md: 16,
  /** Large spacing (24px) - generous spacing */
  lg: 24,
  /** Extra large spacing (32px) - major sections */
  xl: 32,
  /** 2XL spacing (40px) - page sections */
  '2xl': 40,
  /** 3XL spacing (48px) - large gaps */
  '3xl': 48,
} as const;

/**
 * Responsive Breakpoints
 * Mobile-first breakpoint system
 */
export const breakpoints = {
  /** Extra small: 0px - 639px (small phones - iPhone SE, etc.) */
  xs: 0,
  /** Small: 640px - 767px (large phones - iPhone 12, Galaxy S21) */
  sm: 640,
  /** Medium: 768px - 1023px (tablets portrait - iPad) */
  md: 768,
  /** Large: 1024px - 1279px (tablets landscape, small laptops) */
  lg: 1024,
  /** Extra large: 1280px+ (desktop) */
  xl: 1280,
} as const;

/**
 * Safe Area Insets
 * CSS custom properties for device safe areas (notches, home indicators)
 */
export const safeArea = {
  /** Top safe area (notch, status bar) */
  top: 'env(safe-area-inset-top, 0px)',
  /** Right safe area */
  right: 'env(safe-area-inset-right, 0px)',
  /** Bottom safe area (home indicator) */
  bottom: 'env(safe-area-inset-bottom, 0px)',
  /** Left safe area */
  left: 'env(safe-area-inset-left, 0px)',
} as const;

/**
 * Mobile Drawer Dimensions
 * For hamburger menu and side sheets
 */
export const drawer = {
  /** Width of mobile drawer (80% of viewport, max 320px) */
  width: '80vw',
  /** Maximum drawer width */
  maxWidth: 320,
  /** Minimum drawer width */
  minWidth: 240,
  /** Swipe threshold to close (in pixels) */
  swipeThreshold: 20,
  /** Swipe velocity threshold (pixels per second) */
  swipeVelocityThreshold: 300,
} as const;

/**
 * Bottom Sheet Dimensions
 * For filters, selectors, and bottom modals
 */
export const bottomSheet = {
  /** Default height (auto-sized to content) */
  heightAuto: 'auto',
  /** Half screen height */
  heightHalf: '50vh',
  /** Full screen height (minus safe areas) */
  heightFull: `calc(100vh - ${safeArea.top})`,
  /** Handle height for drag indicator */
  handleHeight: 4,
  /** Handle width */
  handleWidth: 32,
  /** Drag threshold to dismiss (in pixels) */
  dragThreshold: 100,
  /** Border radius for top corners */
  borderRadius: 16,
} as const;

/**
 * Card Dimensions for Mobile Lists
 * Replaces table rows on mobile
 */
export const mobileCard = {
  /** Minimum height for list cards */
  minHeight: 80,
  /** Default padding */
  padding: 16,
  /** Spacing between cards */
  gap: 12,
  /** Border radius */
  borderRadius: 12,
  /** Image/avatar size in cards */
  imageSize: 48,
} as const;

/**
 * Form Input Dimensions
 * Optimized for mobile input
 */
export const formInput = {
  /** Minimum height for inputs */
  minHeight: 48,
  /** Recommended height for comfort */
  height: 56,
  /** Padding for text inputs */
  padding: 16,
  /** Border radius */
  borderRadius: 8,
  /** Gap between form fields */
  gap: 16,
} as const;

/**
 * Mobile Typography Scale
 * Scaled for readability on small screens
 */
export const mobileTypography = {
  /** Hero/display text */
  display: {
    fontSize: 32,
    lineHeight: 1.2,
    fontWeight: 700,
  },
  /** Page titles */
  h1: {
    fontSize: 24,
    lineHeight: 1.3,
    fontWeight: 600,
  },
  /** Section titles */
  h2: {
    fontSize: 20,
    lineHeight: 1.4,
    fontWeight: 600,
  },
  /** Card titles */
  h3: {
    fontSize: 18,
    lineHeight: 1.4,
    fontWeight: 600,
  },
  /** Small headers */
  h4: {
    fontSize: 16,
    lineHeight: 1.5,
    fontWeight: 600,
  },
  /** Body text */
  body: {
    fontSize: 14,
    lineHeight: 1.6,
    fontWeight: 400,
  },
  /** Small text, captions */
  caption: {
    fontSize: 12,
    lineHeight: 1.5,
    fontWeight: 400,
  },
  /** Button text */
  button: {
    fontSize: 14,
    lineHeight: 1.5,
    fontWeight: 500,
    textTransform: 'none' as const,
  },
} as const;

/**
 * Animation/Transition Durations
 * For smooth mobile interactions
 */
export const transitions = {
  /** Fast transitions (drawers, tooltips) */
  fast: '150ms',
  /** Normal transitions (hover, focus) */
  normal: '250ms',
  /** Slow transitions (page changes) */
  slow: '400ms',
  /** Easing function for natural motion */
  easing: 'cubic-bezier(0.4, 0.0, 0.2, 1)',
  /** Spring easing for interactive elements */
  spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
} as const;

/**
 * Z-Index Scale
 * Layering for mobile UI
 */
export const zIndex = {
  /** Base content */
  base: 0,
  /** Dropdown menus */
  dropdown: 1000,
  /** Sticky elements */
  sticky: 1020,
  /** Fixed elements (top bar, bottom nav) */
  fixed: 1030,
  /** Drawer/side sheets */
  drawer: 1200,
  /** Modals */
  modal: 1300,
  /** Snackbars/toasts */
  snackbar: 1400,
  /** Tooltips */
  tooltip: 1500,
} as const;

/**
 * Swipe Gesture Configuration
 * For swipe actions and gestures
 */
export const swipeGestures = {
  /** Minimum distance to trigger swipe (in pixels) */
  threshold: 20,
  /** Velocity threshold (pixels per second) */
  velocityThreshold: 300,
  /** Maximum time for swipe gesture (ms) */
  maxTime: 300,
  /** Delta threshold to prevent accidental swipes */
  delta: 10,
  /** Abort threshold - cancel if moved too much in wrong direction */
  abortThreshold: 6,
} as const;

/**
 * Complete Mobile Tokens Export
 * All mobile-specific design tokens in one object
 */
export const mobileTokens = {
  touchTargets,
  bottomNav,
  topBar,
  spacing: mobileSpacing,
  breakpoints,
  safeArea,
  drawer,
  bottomSheet,
  card: mobileCard,
  form: formInput,
  typography: mobileTypography,
  transitions,
  zIndex,
  swipeGestures,
} as const;

// Type exports for TypeScript
export type MobileTokens = typeof mobileTokens;
export type TouchTargets = typeof touchTargets;
export type BottomNav = typeof bottomNav;
export type TopBar = typeof topBar;
export type MobileSpacing = typeof mobileSpacing;
export type Breakpoints = typeof breakpoints;
export type SafeArea = typeof safeArea;
export type Drawer = typeof drawer;
export type BottomSheet = typeof bottomSheet;
export type MobileCard = typeof mobileCard;
export type FormInput = typeof formInput;
export type MobileTypography = typeof mobileTypography;
export type Transitions = typeof transitions;
export type ZIndex = typeof zIndex;
export type SwipeGestures = typeof swipeGestures;
